import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, X, Send, Loader2, ChevronLeft, ChevronRight, Trash2, ImagePlus, Film } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * StoryRing — the horizontal ring of author bubbles that sits at the top of
 * the Feed page. Own bubble first (with a "+ Add" affordance if empty),
 * unseen next, most-recent last.
 *
 * On click of a bubble → opens `<StoryViewer />` in full-screen mode.
 * On click of "+ Add"  → opens `<StoryComposer />`.
 *
 * Data model matches `GET /api/stories/feed` → { rings: [...] }.
 */
const BG_PRESETS = ["#111827", "#E05E36", "#2D5A27", "#2563EB", "#7C3AED",
                    "#DB2777", "#059669", "#F59E0B"];

export function StoryRing() {
  const { user } = useAuth();
  const [rings, setRings] = useState([]);
  const [composer, setComposer] = useState(false);
  const [viewer, setViewer] = useState(null);  // { author_id, index }

  const load = useCallback(async () => {
    try { const r = await api.get("/stories/feed"); setRings(r.data.rings || []); }
    catch { /* silent for unauth */ }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return null;

  const myRing = rings.find(r => r.author_id === user.user_id);
  const others = rings.filter(r => r.author_id !== user.user_id);

  const openRing = (authorId) => setViewer({ author_id: authorId, index: 0 });

  return (
    <>
      <div className="w-full overflow-x-auto no-scrollbar" data-testid="story-ring">
        <div className="flex items-start gap-3 px-4 py-3 min-w-min">
          {/* Own bubble: opens my ring if I have stories, else composer */}
          <button
            onClick={() => (myRing ? openRing(myRing.author_id) : setComposer(true))}
            className="flex flex-col items-center shrink-0 group"
            data-testid="story-my-bubble"
          >
            <div className={`relative w-16 h-16 rounded-full grid place-items-center ${
              myRing ? "bg-gradient-to-br from-emerald-500 to-emerald-700 p-[3px]"
                     : "border-2 border-dashed border-stone-300 bg-stone-50"}`}>
              {myRing ? (
                <div className="w-full h-full rounded-full bg-white grid place-items-center overflow-hidden">
                  {myRing.stories[0]?.media_url && myRing.stories[0]?.media_type === "image" ? (
                    <img src={myRing.stories[0].media_url} alt="" className="w-[54px] h-[54px] rounded-full object-cover"/>
                  ) : (
                    <div className="w-[54px] h-[54px] rounded-full grid place-items-center text-white font-black text-lg"
                         style={{ background: myRing.stories[0]?.background || "#111827" }}>
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              ) : (
                <Plus size={22} className="text-stone-400 group-hover:text-emerald-700 transition-colors" />
              )}
              {!myRing && <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center border-2 border-white">
                <Plus size={11} />
              </div>}
            </div>
            <div className="text-[11px] font-bold text-stone-700 mt-1 max-w-[64px] truncate">Your story</div>
          </button>

          {others.map(r => (
            <button
              key={r.author_id}
              onClick={() => openRing(r.author_id)}
              className="flex flex-col items-center shrink-0 group"
              data-testid={`story-ring-${r.author_id}`}
            >
              <div className={`w-16 h-16 rounded-full grid place-items-center p-[3px] ${
                r.unseen ? "bg-gradient-to-tr from-[#E05E36] via-[#F5B301] to-[#2E9E4B]"
                          : "bg-stone-200"}`}>
                <div className="w-full h-full rounded-full bg-white grid place-items-center overflow-hidden">
                  {r.stories[0]?.media_url && r.stories[0]?.media_type === "image" ? (
                    <img src={r.stories[0].media_url} alt="" className="w-[54px] h-[54px] rounded-full object-cover"/>
                  ) : (
                    <div className="w-[54px] h-[54px] rounded-full grid place-items-center text-white font-black text-lg"
                         style={{ background: r.stories[0]?.background || "#111827" }}>
                      {(r.author_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-[11px] font-bold text-stone-700 mt-1 max-w-[64px] truncate">{r.author_name}</div>
            </button>
          ))}
        </div>
      </div>

      {composer && <StoryComposer onClose={() => setComposer(false)} onPosted={() => { setComposer(false); load(); }}/>}
      {viewer && <StoryViewer ringAuthorId={viewer.author_id} rings={rings} onClose={() => { setViewer(null); load(); }}/>}
    </>
  );
}


const MAX_MEDIA_BYTES = 15 * 1024 * 1024;        // 15 MB per user directive
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif",
                        "video/mp4", "video/webm"];

export function StoryComposer({ onClose, onPosted }) {
  const fileInputRef = useRef(null);
  const [text, setText] = useState("");
  const [background, setBackground] = useState(BG_PRESETS[0]);
  const [busy, setBusy] = useState(false);
  const [media, setMedia] = useState(null);     // { url, type, previewUrl, fileId }

  const pickFile = () => fileInputRef.current?.click();

  const onFilePicked = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      toast.error("Only JPG/PNG/WebP/GIF images or MP4/WebM videos are allowed.");
      return;
    }
    if (file.size > MAX_MEDIA_BYTES) {
      toast.error(`File too large. Max ${MAX_MEDIA_BYTES / (1024 * 1024)} MB.`);
      return;
    }
    setBusy(true);
    try {
      // 1) Multipart upload → /api/files/upload
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", "story-media");
      const up = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fileId = up.data.file_id;
      // 2) Create a public share link (no expiry) → /api/files/{id}/share
      const sh = await api.post(`/files/${fileId}/share`, {});
      const publicUrl = `${api.defaults.baseURL}/files/share/${sh.data.share_id}`;
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      setMedia({
        url:        publicUrl,
        type:       mediaType,
        previewUrl: URL.createObjectURL(file),
        fileId,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Try again.");
    } finally { setBusy(false); }
  };

  const clearMedia = () => {
    if (media?.previewUrl) URL.revokeObjectURL(media.previewUrl);
    setMedia(null);
  };

  const post = async () => {
    if (!text.trim() && !media) { toast.error("Type something or attach media"); return; }
    setBusy(true);
    try {
      await api.post("/stories", {
        text:       text.trim() || null,
        background: media ? "#000000" : background,
        media_url:  media?.url || null,
        media_type: media?.type || null,
      });
      if (media?.previewUrl) URL.revokeObjectURL(media.previewUrl);
      toast.success("Story posted · 24 h");
      onPosted && onPosted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 grid place-items-center p-4" data-testid="story-composer">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <h2 className="font-black text-stone-900">New story</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-100" data-testid="story-composer-close"><X size={16}/></button>
        </div>
        <div className="aspect-[9/12] relative grid place-items-center text-white text-center overflow-hidden"
             style={{ background: media ? "#000" : background }}>
          {media?.type === "image" && <img src={media.previewUrl} alt="preview" className="absolute inset-0 w-full h-full object-cover"/>}
          {media?.type === "video" && <video src={media.previewUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline/>}
          <div className="relative z-10 p-6 w-full">
            <Textarea
              value={text}
              onChange={e => setText(e.target.value.slice(0, 200))}
              placeholder={media ? "Add a caption (optional)" : "What's on your mind?"}
              className={`bg-transparent border-none placeholder:text-white/60 text-xl font-black text-center resize-none focus-visible:ring-0 ${media ? "text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" : "text-white"}`}
              rows={media ? 2 : 4}
              data-testid="story-composer-text"
            />
          </div>
          {media && (
            <button onClick={clearMedia} className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/50 backdrop-blur hover:bg-black/70" data-testid="story-composer-media-clear">
              <X size={14} className="text-white"/>
            </button>
          )}
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={pickFile} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 text-stone-700 text-xs font-bold transition-colors disabled:opacity-40" data-testid="story-composer-pick-media">
              {busy && !media ? <Loader2 size={13} className="animate-spin"/> : <ImagePlus size={13}/>}
              {media ? (media.type === "video" ? "Change video" : "Change photo") : "Add photo / video"}
            </button>
            <input ref={fileInputRef} type="file" accept={ACCEPTED_MIME.join(",")} onChange={onFilePicked} className="hidden" data-testid="story-composer-file-input"/>
            {media?.type === "video" && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-stone-500"><Film size={11}/> Video</span>}
          </div>
          {!media && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1.5">Background</div>
              <div className="flex flex-wrap gap-2">
                {BG_PRESETS.map(bg => (
                  <button
                    key={bg}
                    onClick={() => setBackground(bg)}
                    className={`w-8 h-8 rounded-full transition-transform ${background === bg ? "ring-2 ring-emerald-600 scale-110" : ""}`}
                    style={{ background: bg }}
                    data-testid={`story-bg-${bg}`}
                    aria-label={`Background ${bg}`}
                  />
                ))}
              </div>
            </div>
          )}
          <Button onClick={post} disabled={busy || (!text.trim() && !media)} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 font-black" data-testid="story-composer-post">
            {busy && media ? <Loader2 size={14} className="animate-spin"/> : (<><Send size={14} className="mr-1.5"/> Post to my story</>)}
          </Button>
          <div className="text-center text-[11px] text-stone-400">Auto-expires in 24 hours</div>
        </div>
      </div>
    </div>
  );
}


export function StoryViewer({ ringAuthorId, rings, onClose }) {
  const { user } = useAuth();
  const ring = rings.find(r => r.author_id === ringAuthorId);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!ring) return;
    const s = ring.stories[i];
    if (!s) return;
    // Mark viewed (skip for own).
    if (ring.author_id !== user?.user_id) api.post(`/stories/${s.story_id}/view`).catch(() => {});
    // Videos advance on their own `ended` event; text/image stories use a 5-second timer.
    if (s.media_type === "video") return undefined;
    const t = setTimeout(() => {
      if (i + 1 < ring.stories.length) setI(x => x + 1); else onClose();
    }, 5000);
    return () => clearTimeout(t);
  }, [ring, i, user, onClose]);

  if (!ring) return null;
  const s = ring.stories[i];
  const isMine = ring.author_id === user?.user_id;
  const goNext = () => (i + 1 < ring.stories.length ? setI(i + 1) : onClose());

  const delOwn = async () => {
    if (!isMine || !window.confirm("Delete this story?")) return;
    try {
      await api.delete(`/stories/${s.story_id}`);
      toast.success("Story removed");
      onClose();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black grid place-items-center" data-testid="story-viewer">
      <div className="relative w-full h-full max-w-md mx-auto flex flex-col">
        <div className="absolute top-2 left-0 right-0 px-3 flex gap-1 z-10">
          {ring.stories.map((_, idx) => (
            <div key={idx} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
              <div className={`h-full bg-white transition-all ${idx < i ? "w-full" : idx === i ? "w-full animate-[storyfill_5s_linear]" : "w-0"}`}/>
            </div>
          ))}
        </div>
        <div className="absolute top-6 left-0 right-0 px-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 text-white">
            <div className="w-8 h-8 rounded-full grid place-items-center font-black text-sm bg-white/20">{(ring.author_name || "?").charAt(0).toUpperCase()}</div>
            <div className="font-bold text-sm">{ring.author_name}</div>
          </div>
          <div className="flex items-center gap-2">
            {isMine && <button onClick={delOwn} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid="story-viewer-delete"><Trash2 size={14} className="text-white"/></button>}
            <button onClick={onClose} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid="story-viewer-close"><X size={16} className="text-white"/></button>
          </div>
        </div>
        <div className="flex-1 grid place-items-center text-white text-center relative overflow-hidden"
             style={{ background: s?.background || "#111827" }}>
          {s?.media_type === "image" && (
            <img src={s.media_url} alt="" className="absolute inset-0 w-full h-full object-contain"
                 data-testid="story-viewer-image"/>
          )}
          {s?.media_type === "video" && (
            <video src={s.media_url} className="absolute inset-0 w-full h-full object-contain"
                   autoPlay playsInline controls={false} onEnded={goNext}
                   data-testid="story-viewer-video"/>
          )}
          {s?.text && (
            <div className={`relative z-10 p-6 text-2xl font-black leading-tight max-w-sm ${
              s?.media_url ? "drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" : ""}`}>
              {s.text}
            </div>
          )}
        </div>
        {/* tap zones */}
        <button aria-label="previous" onClick={() => setI(x => Math.max(0, x - 1))} className="absolute inset-y-0 left-0 w-1/3 z-10" data-testid="story-viewer-prev"/>
        <button aria-label="next" onClick={goNext} className="absolute inset-y-0 right-0 w-1/3 z-10" data-testid="story-viewer-next"/>
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4 text-white/60 text-xs">
          <ChevronLeft size={14}/> tap · edges to skip · <ChevronRight size={14}/>
        </div>
      </div>
    </div>
  );
}
